import { Currency } from "@/types/sos-types";
import { AsyncStepGraph } from "../../job-tracking/queries/async-step-graph";
import { BomComponent, getSalesOrderBomComponents } from "../../job-tracking/queries/tracking-data/bom-by-component";
import { BomHierarchyGenerator, BomNode } from "../../job-tracking/queries/tracking-data/bom-hierarchy-generator";
import { BomRecordsForParents, fetchBomRecordsForParents } from "../../job-tracking/queries/tracking-data/bom-records";
import {
  CADCurrencyConverter,
  getCADCurrencyConverter,
  getCurrencies,
  getExchangeRates,
} from "../../job-tracking/queries/tracking-data/currency";
import {
  getTemplatePurchasingPriorities,
  PurchasingPriorities,
} from "../../job-tracking/queries/tracking-data/purchasing-priorities";
import { Estimate, getEstimate } from "./estimate";

export interface EstimateTrackingData {
  exchangeRates: Map<string, number>;
  purchasingPriorities: PurchasingPriorities;
  currencies: Map<number, Currency>;
  CADCurrencyConverter: CADCurrencyConverter;
  estimate: Estimate;
  estimateBomRecords: BomRecordsForParents;
  bomHierarchy: BomNode[];
  bomComponents: BomComponent[];
  bomComponentsByItemId: Map<number, BomComponent>;
}

export async function getEstimateTrackingData(estimateNumber: string): Promise<EstimateTrackingData> {
  const graph = getEstimateTrackingGraph(estimateNumber);
  return await graph.runAll();
}

function getEstimateTrackingGraph(estimateNumber: string) {
  const graph = new AsyncStepGraph<EstimateTrackingData>();

  graph.define("exchangeRates", [], () => getExchangeRates());
  graph.define("purchasingPriorities", [], () => getTemplatePurchasingPriorities());
  graph.define("currencies", ["exchangeRates"], ({ exchangeRates }) => getCurrencies(exchangeRates));
  graph.define("CADCurrencyConverter", ["currencies"], ({ currencies }) => getCADCurrencyConverter(currencies));
  graph.define("estimate", ["CADCurrencyConverter"], ({ CADCurrencyConverter }) =>
    getEstimate(estimateNumber, CADCurrencyConverter),
  );
  graph.define("estimateBomRecords", ["estimate", "CADCurrencyConverter"], ({ estimate, CADCurrencyConverter }) =>
    fetchBomRecordsForParents(
      estimate.lines.map(({ item, baseQuantity: quantity }) => ({ item, quantity })),
      CADCurrencyConverter,
    ),
  );
  graph.define(
    "bomHierarchy",
    ["estimateBomRecords", "purchasingPriorities"],
    ({ estimateBomRecords, purchasingPriorities }) =>
      new BomHierarchyGenerator(purchasingPriorities).generate(estimateBomRecords),
  );

  graph.define("bomComponents", ["bomHierarchy"], ({ bomHierarchy }) => getSalesOrderBomComponents(bomHierarchy, []));
  graph.define(
    "bomComponentsByItemId",
    ["bomComponents"],
    ({ bomComponents }) => new Map(bomComponents.map((x) => [x.item.id, x])),
  );
  return graph;
}
